import React, { FC, useCallback, useEffect } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';
import { useWatch } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import { v4 as uuidv4 } from 'uuid';

import { CloseIcon, CloudUploadIcon, InfoCircle, UploadIcon } from '../../../../assets/icons/index';
import LoaderIcon from '../../../../assets/icons/loader-icon';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import Button from '../../../../components/button/index';
import { InfoBlade } from '../../../../components/info-blade/index';
import { FormTextarea } from '../../../../components/textarea/index';
import { BackendRoute } from '../../../../config/index';
import { useLocale } from '../../../../hooks/index';
import { Locale, StringKey } from '../../../../lang/index';
import { ApiService } from '../../../../services/index';
import { EventTypeBackend } from '../../../../types/events.types';
import {
  fileWithStorageDisplayName,
  getFileDisplayName,
} from '../../../../utils/file-upload-duplicate-names.util';
import { getTranslation } from '../../../../utils/get-translation.util';
import { isExerciseBackedEventType } from '../../utils/exercise-event-edit-enabled.util';
import {
  buildStakeholderDocumentBuckets,
  EventDocumentsSection,
} from '../components/event-documents-section';
import { EventDocusignLinkedFilesSection } from '../components/event-docusign-linked-files';
import {
  EventFileDuplicateFeedback,
  EventFileUploadRenameModal,
  EventFileUploadStatusIcon,
} from '../components/event-file-upload-duplicate-ui';
import { ChildrenFormProps } from '../event-form.types';
import { FormSchema, stepTwo as stepTwoFormSchema } from '../event-form.validation';
import { useEventFileUploadDuplicates } from '../hooks/use-event-file-upload-duplicates';
import {
  isDocusignFileLink,
  resolveEventAllDocusignFileLinks,
  resolveEventLevelDocusignFileLinks,
  resolveEventLevelUploadDocusignFileLinks,
} from '../utils/resolve-linked-signature-file-links.util';

export type StepTwoProps = ChildrenFormProps;

const fileUploadInfoBlade: PartialRecord<EventTypeBackend, StringKey> = {
  [EventTypeBackend.VALUATION]: 'VALUATION_DOCUMENT_INFO_BLADE',
  [EventTypeBackend.CONVERSION]: 'CLASS_CONVERSION_DOCUMENT_INFO_BLADE',
  [EventTypeBackend.SHARE_ISSUANCE]: 'SHARE_ISSUANCE_DOCUMENT_INFO_BLADE',
};

const retrieveEventTypeFromForm = ({
  eventDetails: { conversion, valuation, 'share-issuance': shareIssuance },
}: FormSchema['stepOne']) => {
  if (valuation) return EventTypeBackend.VALUATION;
  if (conversion) return EventTypeBackend.CONVERSION;
  if (shareIssuance) return EventTypeBackend.SHARE_ISSUANCE;

  return null;
};

export const StepTwo: FC<StepTwoProps> = ({
  handleCloseModal,
  control,
  setFormData,
  nextFormStep,
  prevFormStep,
  formData,
  lockMode,
  clearErrors,
  setError,
  isLoading: isPendingSave,
  errors: allErrors,
  isGrantEditWizard,
  showDocumentsStepBack,
  companyId,
  event,
}) => {
  const { optionalStep, stepTwo, stepOne } = useWatch<FormSchema>({ control });
  const { stepTwo: errors } = allErrors;
  const { success } = stepTwoFormSchema.safeParse(stepTwo);
  const { messagesLocale, locale } = useLocale();

  useEffect(() => {
    clearErrors('stepTwo.files');
  }, [clearErrors]);

  const type = retrieveEventTypeFromForm(stepOne as FormSchema['stepOne']);
  const files = stepTwo?.files || [];
  const isExerciseFamilyEvent = Boolean(event?.type && isExerciseBackedEventType(event.type));
  const generalDocusignLinks = isExerciseFamilyEvent
    ? resolveEventAllDocusignFileLinks(event)
    : resolveEventLevelDocusignFileLinks(event);
  const knownFileLinks = [
    ...(event?.filesLinks ?? []),
    ...files.map((file) => file.docLink).filter((docLink): docLink is string => Boolean(docLink)),
  ];
  const linkedDocusignFileLinks = isExerciseFamilyEvent
    ? generalDocusignLinks
    : resolveEventLevelUploadDocusignFileLinks(event, knownFileLinks);
  const manualFiles = files.filter(
    (file) => !file.docLink || !isDocusignFileLink(file.docLink, generalDocusignLinks),
  );
  const allFilesFinishedUploading = files.every((file) => !file.doc || Boolean(file.docLink));
  // Share Issuance attaches documents per stakeholder as well as per event, so its Documents step
  // is the accordion section rather than the single event-level dropzone every other type uses.
  const issuanceItems = optionalStep?.['share-issuance']?.issuanceItems ?? [];
  const isShareIssuanceDocuments = type === EventTypeBackend.SHARE_ISSUANCE;
  const stakeholderBuckets = buildStakeholderDocumentBuckets(
    issuanceItems,
    (index) => `optionalStep.share-issuance.issuanceItems.${index}.files`,
    messagesLocale['ISSUANCE'],
  );
  // Without this the Next button ignores uploads still in flight in the per-stakeholder buckets,
  // which live under optionalStep rather than in the stepTwo.files the gate above looks at.
  const stakeholderFilesSettled = issuanceItems.every((item) =>
    (item?.files ?? []).every((file) => !file.doc || Boolean(file.docLink)),
  );

  const isValid =
    success &&
    allFilesFinishedUploading &&
    stakeholderFilesSettled &&
    (files.some(({ docLink }) => docLink) || files.length === 0);
  const uploadedFiles = manualFiles.reduce((prev, curr) => (curr.docLink ? prev + 1 : prev), 0);
  const isReachedUploadMaximum = uploadedFiles >= 20;

  const getStepTwoFilesData = useCallback(() => formData('stepTwo.files') || [], [formData]);

  const {
    hasUnresolvedDuplicateNames,
    isFileNameDuplicate,
    removeFileToRename,
    renameModal,
    renameModalFolderNames,
    resolveDisplayName,
    validateDropFiles,
  } = useEventFileUploadDuplicates({
    files: manualFiles,
    onRenameInForm: ({ currentName, newName }) => {
      const currentFiles = getStepTwoFilesData();
      setFormData(
        'stepTwo.files',
        currentFiles.map((file) =>
          getFileDisplayName(file.doc) === currentName
            ? {
                ...file,
                doc:
                  file.doc instanceof File
                    ? new File([file.doc], newName, { type: file.doc.type })
                    : { ...file.doc, name: newName },
              }
            : file,
        ),
        { shouldValidate: true },
      );
    },
  });

  const onDrop = useCallback(
    async (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      if (fileRejections.length > 0) {
        fileRejections.forEach(({ file, errors }) => {
          errors.forEach((e) => {
            if (e.code === 'file-invalid-type') {
              setError('stepTwo.files', {
                type: 'manual',
                message: `${messagesLocale['INVALID_FILE_TYPE'].replace('{fileType}', file.name)}`,
              });
            }
          });
        });
        return;
      }

      if (isReachedUploadMaximum) return;
      const existingFiles = getStepTwoFilesData();

      const duplicateFile = validateDropFiles(acceptedFiles, existingFiles);
      if (duplicateFile) {
        setError('stepTwo.files', {
          type: 'manual',
          message: messagesLocale['FILE_EXISTS'],
        });
        return;
      }

      const filesToUpload = acceptedFiles.slice(0, 20 - existingFiles.length);

      filesToUpload.forEach(async (file) => {
        const { callback, abort } = ApiService.uploadFile<string>();
        const id = uuidv4();
        const { error, success } = stepTwoFormSchema.safeParse({
          files: [{ doc: file, docLink: '', loadProgress: 1, abort, id }],
        });

        if (!success) return setError('stepTwo.files', error.issues[0]);

        if (success) clearErrors('stepTwo.files');

        const existingFiles = getStepTwoFilesData();

        setFormData(
          'stepTwo.files',
          [...existingFiles, { doc: file, docLink: '', loadProgress: 1, abort, id }],
          { shouldValidate: true },
        );

        const link = await callback(
          { endpoint: BackendRoute.SHARE_CLASSES, routePath: ['file'] },
          ['file', file],
          {
            onUploadProgress: (n) => {
              const currentFiles = getStepTwoFilesData();
              setFormData(
                'stepTwo.files',
                currentFiles.map((f) => (f.id === id ? { ...f, loadProgress: n } : f)),
              );
            },
            onAboard: () => {
              const currentFiles = getStepTwoFilesData();
              setFormData(
                'stepTwo.files',
                currentFiles.filter((f) => f.id !== id),
              );
            },
          },
        );

        if (!link) return;
        const currentFiles = getStepTwoFilesData();
        setFormData(
          'stepTwo.files',
          currentFiles.map((f) =>
            f.id === id
              ? {
                  ...f,
                  loadProgress: 100,
                  docLink: link,
                  doc: fileWithStorageDisplayName(file, link),
                }
              : f,
          ),
        );
      });
    },
    [
      clearErrors,
      getStepTwoFilesData,
      isReachedUploadMaximum,
      messagesLocale,
      setError,
      setFormData,
      validateDropFiles,
    ],
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: true,
    disabled: isReachedUploadMaximum,
    onDropRejected: () =>
      setError('stepTwo.files', {
        message: getTranslation('FILE_FORMAT_NOT_SUPPORTED'),
        type: 'validate',
      }),
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/vnd.ms-excel': ['.xlsx'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xls'],
      'text/csv': ['.csv'],
      'image/png': ['.png'],
      'image/jpeg': ['.jpeg', '.jpg'],
    },
  });

  const handleDeleteFile = useCallback(
    (id: string) => {
      const current = (formData('stepTwo.files') || []).find((file) => file.id === id);
      removeFileToRename(getFileDisplayName(current?.doc));

      if (current && !current.docLink) current.abort();

      const currentData = (formData('stepTwo.files') || []).filter((file) => file.id !== id);
      setFormData('stepTwo.files', currentData);
      if (currentData.length === 0) return clearErrors('stepTwo.files');
    },
    [clearErrors, formData, removeFileToRename, setFormData],
  );

  return (
    <>
      <EventFileUploadRenameModal
        currentName={renameModal.currentName}
        folderNames={renameModalFolderNames}
        isOpen={renameModal.isOpenRenameModal}
        onClose={renameModal.handleCloseRenameModal}
        onOpenChange={renameModal.renameModalToggler}
        onSuccess={renameModal.handleSuccessRenameModal}
      />
      <div className="invisible-y-scroll flex h-full flex-col gap-4 rounded-b-xl border-b border-gray-300 bg-white p-6 pt-0">
        {!isShareIssuanceDocuments && (
          <span className="text-xs font-[450] text-gray-500">
            <AppFormattedMessage id="DOCUMENTS_UPLOAD" />
          </span>
        )}
        {type && fileUploadInfoBlade[type] && (
          <InfoBlade
            className={locale === Locale.FR && type === EventTypeBackend.CONVERSION ? 'px-2' : ''}
            text={messagesLocale[fileUploadInfoBlade[type]]}
          />
        )}
        {isShareIssuanceDocuments && (
          <EventDocumentsSection
            clearErrors={clearErrors}
            errors={allErrors}
            formData={formData}
            generalField="stepTwo.files"
            setError={setError}
            setFormData={setFormData}
            stakeholderBuckets={stakeholderBuckets}
          />
        )}
        {!isShareIssuanceDocuments && (
          <input
            {...getInputProps()}
            accept=".pdf, .doc, .docx, .xlsx, .xls, .csv, .png, .jpeg, .jpg"
            className="hidden"
            multiple
            type="file"
          />
        )}
        {!isShareIssuanceDocuments && (
          <>
            <label
              {...getRootProps()}
              className={twMerge(
                'relative flex w-full items-center gap-6 rounded-lg border border-dashed border-gray-200 p-6',
                isReachedUploadMaximum ? 'cursor-auto' : 'cursor-pointer',
              )}
            >
              {errors?.files ? (
                <InfoCircle className="size-14" thinIconVariant />
              ) : (
                <CloudUploadIcon />
              )}
              <div className="flex flex-col gap-2">
                <div className="flex">
                  {errors?.files ? (
                    <span className="text-sm font-[550] text-fireside-600">
                      {errors?.files.message?.toString()}
                    </span>
                  ) : (
                    <p className="text-sm font-medium text-gray-700">
                      <span className="underline">
                        <AppFormattedMessage id="CLICK_TO_UPLOAD" />{' '}
                      </span>
                      <AppFormattedMessage id="OR_DRAG_AND_DROP" />
                    </p>
                  )}
                </div>

                <div className="flex flex-col gap-1 text-label-sm font-[450] text-gray-500">
                  <span>
                    <AppFormattedMessage
                      id="SUPPORTED_FORMATS"
                      values={{
                        formats: 'doc/docx, xls/xlsx, csv, jpeg/jpg, png or pdf',
                      }}
                    />
                  </span>
                  <span>
                    <AppFormattedMessage id="MAXIMUM_FILE_SIZE" values={{ size: 10 }} />
                  </span>
                </div>
                {errors?.files && (
                  <span className="flex h-9 w-fit items-center gap-1 rounded border border-gray-100 bg-gray-25 px-3 py-[6px] text-sm font-[450] text-gray-700">
                    <UploadIcon />
                    <AppFormattedMessage id="UPLOAD_AGAIN" />
                  </span>
                )}
              </div>
            </label>
            {(manualFiles?.length || 0) > 0 && (
              <div className="flex flex-col gap-2">
                <span className="pb-2 text-xs font-[450] text-gray-500">
                  {uploadedFiles > 1 ? (
                    <AppFormattedMessage
                      id="COUNT_FILES_UPLOADED"
                      values={{ count: uploadedFiles }}
                    />
                  ) : (
                    <AppFormattedMessage id="ONE_FILE_UPLOADED" />
                  )}
                </span>
                {manualFiles?.map(({ doc, docLink, loadProgress, id }, i) => (
                  <div className="flex flex-col gap-2" key={`${id}_${docLink}`}>
                    <div className="flex flex-col overflow-hidden rounded border border-brand-50 bg-brand-25">
                      <div className="flex justify-between p-3" key={`${doc?.name}_${i}`}>
                        <div className="flex items-center gap-2 overflow-hidden">
                          <EventFileUploadStatusIcon
                            docLink={docLink}
                            isDuplicate={isFileNameDuplicate(doc, docLink)}
                            loadingIcon={<LoaderIcon className="size-4" />}
                          />
                          <span className="truncate text-xs font-[450] text-gray-700">
                            {resolveDisplayName(doc)}
                          </span>
                        </div>
                        <Button
                          className="w-fit p-1 hover:bg-gray-50"
                          onClick={() => handleDeleteFile(id || '')}
                          styleType="NONE"
                          type="button"
                        >
                          <CloseIcon className="size-2" />
                        </Button>
                      </div>
                      <div
                        className={twMerge(
                          'h-[2px] shrink-0 bg-brand-700 transition-all duration-700',
                          docLink && 'bg-transparent',
                        )}
                        style={{ width: (loadProgress || (docLink && 100)) + '%' }}
                      />
                    </div>
                    <EventFileDuplicateFeedback
                      doc={doc}
                      docLink={docLink}
                      isDuplicate={isFileNameDuplicate(doc, docLink)}
                      onRename={renameModal.handleOpenRenameModal}
                    />
                  </div>
                ))}
              </div>
            )}
          </>
        )}
        <EventDocusignLinkedFilesSection
          companyId={companyId}
          fileLinks={linkedDocusignFileLinks}
        />
        <FormTextarea
          control={control}
          name="stepTwo.additionalNotes"
          placeholder={<AppFormattedMessage id="ADDITIONAL_NOTES_OPTIONAL" />}
          styleVariant="DEFAULT_W_PLACEHOLDER"
          wrapperClassName="bg-gray-50"
        />
      </div>

      <div className="flex h-9 w-full justify-between gap-3 px-6">
        <Button
          className="w-fit bg-transparent px-4 py-[6px] text-sm font-[450] text-gray-700"
          onClick={handleCloseModal}
          styleType="NONE"
        >
          <AppFormattedMessage id="CANCEL" />
        </Button>
        <div className="flex gap-3">
          {(!lockMode || isGrantEditWizard || showDocumentsStepBack) && (
            <Button
              className="h-full w-fit rounded border border-gray-100 bg-white px-3 py-[6px] text-sm font-[450] text-gray-700 shadow-xs"
              onClick={prevFormStep}
              styleType="NONE"
              type="button"
            >
              <AppFormattedMessage id="BACK" />
            </Button>
          )}
          <Button
            className="h-full w-fit px-4 py-[6px] text-sm font-[550] text-white"
            disabled={!isValid || hasUnresolvedDuplicateNames}
            isLoading={isPendingSave}
            onClick={nextFormStep}
            type="button"
          >
            {isGrantEditWizard ? (
              <AppFormattedMessage id="UPDATE" />
            ) : lockMode ? (
              <AppFormattedMessage id="UPDATE" />
            ) : !lockMode && (stepTwo?.additionalNotes || stepTwo?.files?.length || 0 > 0) ? (
              'Review'
            ) : (
              <AppFormattedMessage id="SKIP" />
            )}
          </Button>
        </div>
      </div>
    </>
  );
};
